import { withDefault } from "collection-utils";

import { SerializedComponent } from "../../components/SerializedApp";
import { ArrayScreenFormat, ScreenDescriptionKind, PropertyDescription, TableGlideType } from "../description";
import { PropertyDescriptor, ComponentBuilder } from "../components";
import { getEnumProperty, makeEnumProperty, makeSwitchProperty } from "../components/property-utils";
import { ImageAspectRatio, TilesPadding, Corners, TextPosition, OverlayTextAlignment } from "../components/image-types";
import {
    SummaryArrayScreenDescription,
    FallbackInitialsSummaryArrayContentDescription,
    FallbackInitialsSummaryArrayScreenHandler,
    isolateFallbackInitialsSummaryArrayContentDescription,
} from "./summary-array-screen";
import { makeImageHeightPropertyHandler } from "../components/property-handlers";
import { getFeatureSetting } from "../../feature-settings";
import { FixedArrayContent } from "./array-content";
import { TextSize } from "../components/text-types";
import { TilesArrayContentDescription } from "./tiles-array-screen";

export interface GridArrayContentDescription extends FallbackInitialsSummaryArrayContentDescription {
    readonly size: PropertyDescription | undefined;
}

interface GridArrayScreenDescription extends SummaryArrayScreenDescription, GridArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly format: ArrayScreenFormat.Grid;
}

export class GridArrayScreenHandler extends FallbackInitialsSummaryArrayScreenHandler<
    GridArrayContentDescription,
    GridArrayScreenDescription
> {
    protected readonly supportsNonURLImages = true;
    protected readonly supportsEmojiImages = false;
    protected readonly supportsTruncateList = false;
    protected readonly fullTitleFallback = true;

    constructor() {
        super(ArrayScreenFormat.Grid, "Old Tiles", "grid", true);
    }

    public get isLegacy(): boolean {
        return !getFeatureSetting("preTileScreens");
    }

    protected contentWithFallbackInitials(
        { b }: ComponentBuilder,
        desc: GridArrayScreenDescription,
        _table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        initials: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined
    ): SerializedComponent {
        const size = getEnumProperty<ImageAspectRatio>(desc.size) ?? ImageAspectRatio.ThreeByOne;
        return b.ui("Grid", {
            list: arrayData,
            item: b.gridItem(title, subtitle, image, size, initials, onTap, onDropImage),
        });
    }

    public getAdditionalPropertyDescriptors(): ReadonlyArray<PropertyDescriptor> {
        return [makeImageHeightPropertyHandler(ImageAspectRatio.ThreeByOne, false, false, false)];
    }

    public fixContentDescription(gridContent: GridArrayContentDescription): FixedArrayContent | undefined {
        return {
            properties: {
                ...isolateFallbackInitialsSummaryArrayContentDescription(gridContent),
                size: makeEnumProperty(withDefault(getEnumProperty(gridContent.size), ImageAspectRatio.ThreeByOne)),
                numColumns: makeEnumProperty(1),
                padding: makeEnumProperty(TilesPadding.Loose),
                isHorizontal: makeEnumProperty(false),
                allowWrapping: makeSwitchProperty(true),
                corners: makeEnumProperty(Corners.Rounded),
                textPosition: makeEnumProperty(TextPosition.Below),
                overlayTextAlignment: makeEnumProperty(OverlayTextAlignment.Center),
                textSize: makeEnumProperty(TextSize.Medium),
                allCaps: makeSwitchProperty(false),
            } as TilesArrayContentDescription,
            format: ArrayScreenFormat.Tiles,
        };
    }
}
